import classNames from 'classnames';
import type { ReactNode, RefObject } from 'react';

export const CAMERA_OFF_PILL_CLASS =
  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[rgba(0,0,0,0.7)] text-[#fff] text-xs lg:text-base px-6 py-3 rounded-[16px] z-[1] pointer-events-none';

/** Снимает последний кадр видео на canvas с blur. Возвращает false, если кадра ещё нет. */
export function captureBlurredVideoFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  options?: { mirror?: boolean },
): boolean {
  if (!video.videoWidth || !video.videoHeight) return false;

  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
  canvas.style.filter = 'blur(60px)';

  if (options?.mirror) {
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();
  } else {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  }

  return true;
}

type CameraOffBackdropProps = {
  visible: boolean;
  hasCapturedFrame: boolean;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  className?: string;
  objectFit?: 'cover' | 'contain';
};

/** Фон при выключенной камере: blurred last frame или запасной градиент. */
export function CameraOffBackdrop({
  visible,
  hasCapturedFrame,
  canvasRef,
  className,
  objectFit = 'cover',
}: CameraOffBackdropProps) {
  return (
    <>
      <canvas
        ref={canvasRef}
        className={classNames('w-full h-full absolute top-0 left-0 z-0', className)}
        style={{
          visibility: visible && hasCapturedFrame ? 'visible' : 'hidden',
          objectFit,
        }}
      />
      {visible && !hasCapturedFrame && (
        <div className={classNames('absolute inset-0 z-0 overflow-hidden', className)} aria-hidden>
          <div
            className="absolute inset-[-25%] bg-[radial-gradient(ellipse_at_35%_40%,#c9bdb4_0%,#8f8984_42%,#3f3c45_100%)]"
            style={{ filter: 'blur(60px)' }}
          />
        </div>
      )}
    </>
  );
}

type CameraOffLabelProps = {
  visible: boolean;
  children: ReactNode;
  className?: string;
};

export function CameraOffLabel({ visible, children, className }: CameraOffLabelProps) {
  return (
    <div
      className={classNames(CAMERA_OFF_PILL_CLASS, className)}
      style={{ visibility: visible ? 'visible' : 'hidden' }}
    >
      {children}
    </div>
  );
}
